import { zodResolver } from "@hookform/resolvers/zod";
import { Button, TextField } from "@mui/material";
import { BedIcon, PlusCircleIcon, XIcon } from "@phosphor-icons/react";
import { times } from "lodash-es";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { useTranslations } from "use-intl";
import { object } from "zod";

import { Card } from "@/components/Card/Card";
import { CounterButton } from "@/components/CounterButton/CounterButton";
import { TravellerTypeSelectionItem } from "@/components/TravellerTypeSelectionItem/TravellerTypeSelectionItem";
import type { TRoom } from "@/features/hotels/types/types";
import {
  MAX_CHILDREN_PER_ROOM,
  MAX_VISITORS_PER_ROOM,
} from "@/features/hotels/utils/constants";
import {
  getRoomsValidationSchema,
  type HotelSearchFormValues,
} from "@/features/hotels/utils/hotelsValidation";
import { Feature, isFeatureEnabled } from "@/utils/featureFlags";

const newRoom: TRoom = {
  adults: 1,
  children: 0,
  childrenAges: [],
};

type Props = {
  value: HotelSearchFormValues["rooms"];
  onConfirm: (values: HotelSearchFormValues["rooms"]) => void;
};

export function VisitorsSelection({ value, onConfirm }: Props) {
  const t = useTranslations();

  const methods = useForm<Pick<HotelSearchFormValues, "rooms">>({
    defaultValues: {
      rooms: value,
    },
    resolver: zodResolver(
      object({
        rooms: getRoomsValidationSchema(t),
      }),
    ),
  });

  const { handleSubmit, control } = methods;

  const {
    fields: rooms,
    remove,
    update,
    append,
  } = useFieldArray({
    control,
    name: "rooms",
  });

  return (
    <FormProvider {...methods}>
      <form
        noValidate
        onSubmit={(event) => {
          event.stopPropagation();

          handleSubmit((data) => {
            onConfirm(data.rooms);
          })(event);
        }}
        className="flex h-full w-full flex-col"
      >
        <div className="mb-3 text-sm text-text-primary">
          {t("hotels.search.guestsPerRoomInformation", {
            maxGuestsPerRoom: MAX_VISITORS_PER_ROOM,
          })}
        </div>

        <div className="space-y-3">
          {rooms.map((room, roomIndex) => {
            const maxChildCount = Math.min(
              MAX_VISITORS_PER_ROOM - (room.adults ?? 1),
              MAX_CHILDREN_PER_ROOM,
            );
            const maxAdultCount = MAX_VISITORS_PER_ROOM - (room.children ?? 0);

            return (
              <Card
                key={room.id}
                icon={
                  <BedIcon
                    size={24}
                    color="var(--color-surface-pressed-accent-strong)"
                  />
                }
                title={
                  isFeatureEnabled(Feature.MultiRoomBooking)
                    ? `${t("hotels.search.room")} ${roomIndex + 1}`
                    : t("hotels.search.room")
                }
                action={
                  rooms.length > 1 ? (
                    <Button
                      variant="text"
                      color="error"
                      onClick={() => remove(roomIndex)}
                      sx={{
                        fontWeight: "var(--font-weight-medium)",
                        py: "calc(var(--spacing)*1)",
                        px: "calc(var(--spacing)*2)",
                        my: "calc(var(--spacing)*-1)",
                      }}
                    >
                      {t("remove")}
                    </Button>
                  ) : null
                }
              >
                <div className="-my-3 divide-y divide-stroke-neutral-soft *:py-3">
                  <TravellerTypeSelectionItem
                    label={t("hotels.search.adult")}
                    description={t("hotels.search.twelveYearOldPlus")}
                  >
                    <CounterButton
                      value={room.adults ?? 1}
                      onChange={(value) =>
                        update(roomIndex, {
                          ...room,
                          adults: value,
                        })
                      }
                      minValue={1}
                      maxValue={maxAdultCount}
                      minusButtonAriaLabel={t("hotels.search.removeAdult")}
                      plusButtonAriaLabel={t("hotels.search.addAdult")}
                    />
                  </TravellerTypeSelectionItem>

                  <div>
                    <TravellerTypeSelectionItem
                      label={t("hotels.search.child")}
                      description={t("hotels.search.underTwelveYearOld")}
                    >
                      <CounterButton
                        value={room.children ?? 0}
                        onChange={(value) => {
                          const nextAges = Array.from(
                            { length: value },
                            (_, i) => room.childrenAges?.[i],
                          );
                          update(roomIndex, {
                            ...room,
                            children: value,
                            childrenAges: nextAges,
                          });
                        }}
                        maxValue={maxChildCount}
                        minusButtonAriaLabel={t("hotels.search.removeChild")}
                        plusButtonAriaLabel={t("hotels.search.addChild")}
                      />
                    </TravellerTypeSelectionItem>

                    {/* Children ages */}
                    <div className="grid grid-cols-2 gap-2 not-empty:mt-4">
                      {times(room.children, (childIndex) => {
                        return (
                          <Controller
                            key={childIndex}
                            control={control}
                            name={`rooms.${roomIndex}.childrenAges.${childIndex}`}
                            render={({ field, fieldState }) => (
                              <TextField
                                label={t("hotels.search.childNAge", {
                                  number: childIndex + 1,
                                })}
                                type="text"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                value={field.value ?? ""}
                                onChange={(e) => {
                                  const raw = e.target.value;

                                  const numberValue = raw
                                    ? Number(raw)
                                    : undefined;

                                  const nextValue =
                                    !numberValue || Number.isNaN(numberValue)
                                      ? raw
                                      : numberValue;

                                  field.onChange(nextValue);
                                }}
                                slotProps={{
                                  htmlInput: {
                                    inputMode: "numeric",
                                    pattern: "d*",
                                  },
                                  input: {
                                    endAdornment: field.value ? (
                                      <button
                                        className="shrink-0"
                                        type="button"
                                        onClick={() => {
                                          field.onChange("");
                                        }}
                                      >
                                        <XIcon
                                          size={20}
                                          className="fill-text-placeholder"
                                        />
                                      </button>
                                    ) : null,
                                  },
                                }}
                              />
                            )}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {isFeatureEnabled(Feature.MultiRoomBooking) ? (
          <Button
            type="button"
            variant="text"
            startIcon={<PlusCircleIcon size={24} />}
            sx={{ my: "calc(var(--spacing)*3)", alignSelf: "center" }}
            onClick={(e) => {
              append(newRoom);
              e.currentTarget.blur();
            }}
          >
            {t("hotels.search.addRoom")}
          </Button>
        ) : null}
        <Button
          fullWidth
          type="submit"
          variant="contained"
          sx={{
            marginTop: "auto",
          }}
        >
          {t("confirm")}
        </Button>
      </form>
    </FormProvider>
  );
}
